<template>
	<ValidationObserver ref="validationObserver" v-slot="{ handleSubmit }" tag="div">
		<vue-recaptcha
			v-if="captcha.key"
			ref="invisibleReCaptcha"
			size="invisible"
			:language="country"
			:load-recaptcha-script="false"
			:sitekey="captcha.key"
			@verify="handleCaptchaResponse"
		/>
		<form class="form" autocomplete="on" novalidate @submit.prevent="handleSubmit(handleSubmitClick)">
			<template v-if="form.globalMessage">
				<Error v-for="(msg, i) in form.globalMessage" :key="msg + i" class="mb-4">
					{{ msg }}
				</Error>
			</template>
			<BaseInput
				v-model="form.email"
				:has-error="!!form.globalMessage"
				:rules="isLogin ? 'email' : 'email-no-special-chars'"
				name="j_username"
				type="email"
				:label="labels.email"
				class="mb-3"
				required
			/>
			<BaseInput
				v-model="form.password"
				:has-error="!!form.globalMessage"
				rules="password"
				name="j_password"
				type="password"
				:label="labels.password"
				class="mb-8"
				required
			/>
			<div v-if="!isLogin" class="note mb-6">
				<SvgIcon icon-name="info" class="mr-2" />
				{{ labels.registration.password_text }}
			</div>
			<button
				type="submit"
				:data-track-element="isLogin ? 'Login' : 'Registration' + 'BtnClick'"
				class="o-btn o-btn--full o-btn--no-spacing c-btn--normal mb-6"
				:disabled="waitingForResponse"
				@click="$emit('sendTracking', $event)"
			>
				{{ labels[section].submit_button }}
			</button>

			<template v-if="isLogin">
				<div class="mb-4">
					<a
						class="text-link"
						href="/my-account/forgotten-password"
						data-track-element="PasswordForgotten"
						@click="$emit('sendTracking', $event)"
					>
						{{ labels.login.forgot_password }}
					</a>
				</div>
				<div>
					{{ labels.login.app_user }}
					<a
						class="text-link"
						href="/my-account/forgotten-password"
						data-track-element="AppPassword"
						@click="$emit('sendTracking', $event)"
					>
						{{ labels.login.create_password }}
					</a>
				</div>
			</template>
		</form>
	</ValidationObserver>
</template>

<script>
import Error from "@/components/Error";
import BaseInput from "@/components/BaseInput";
import { country } from "@ocm/services/ocm-object";
import labels from "../labels.json";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon";
import { ValidationObserver } from "vee-validate";
import { VueRecaptcha } from "vue-recaptcha";
import { requestLoginRegistration } from "@/services/rest";

export default {
	components: {
		Error,
		BaseInput,
		SvgIcon,
		ValidationObserver,
		VueRecaptcha,
	},
	props: {
		isLogin: { type: Boolean, required: true, default: true },
	},
	data: () => ({
		country,
		csrfTokenAttempts: 0,
		labels,
		section: "login",
		form: {
			email: "",
			password: "",
			globalMessage: null,
		},
		captcha: {
			response: null,
			key: ocm?.captchaOptions?.publickey,
		},
		waitingForResponse: false,
	}),
	computed: {
		csrfToken: () => ocm?.config?.CSRFToken,
	},
	watch: {
		isLogin() {
			this.section = this.isLogin ? "login" : "registration";
			this.resetForm();
		},
	},
	mounted() {
		this.section = this.isLogin ? "login" : "registration";
		this.resetForm();
	},
	methods: {
		resetForm() {
			this.resetValidation();
			this.form.password = "";
			this.form.globalMessage = null;
		},
		resetValidation() {
			// vee-validate: / https://logaretm.github.io/vee-validate/guide/forms.html#programmatic-access-with-refs
			this.$nextTick(() => {
				this.$refs.validationObserver?.reset();
			});
		},
		clearFormData() {
			this.resetForm();
			this.form.email = "";
		},
		handleSubmitClick() {
			if (this.captcha.key) {
				this.$refs.invisibleReCaptcha.execute();
				return;
			}
			this.submitForm();
		},
		handleCaptchaResponse(response) {
			this.captcha.response = response;
			this.$refs.invisibleReCaptcha.reset();
			this.submitForm();
		},
		submitForm() {
			this.form.globalMessage = null;
			this.waitingForResponse = true;

			const authData = {
				j_username: this.form.email,
				j_password: this.form.password,
				rememberMe: true,
				CSRFToken: this.csrfToken,
				g_recaptcha_response: this.captcha.response,
			};

			requestLoginRegistration(authData, this.section, this.isLogin)
				.catch((e) => {
					if (e.response.status === 412) {
						this._handleCsrfError(e.response.headers.csrftoken);
					} else if (e.response.data.globalErrorMessages) {
						this.form.globalMessage = e.response.data.globalErrorMessages;
					}
				})
				.finally(() => {
					this.waitingForResponse = false;
				});
		},
		_handleCsrfError(freshToken) {
			if (++this.csrfTokenAttempts < 3) {
				this.csrfToken = freshToken;
				this.submitForm();
			} else {
				this.form.globalMessage = [labels.errorMessages.loginFailed_AllowCookies];
			}
		},
	},
};
</script>
